import { type ComponentProps, memo, useMemo } from 'react';

import { cn } from '@air/utils-tailwind';

import { type CreditUsageCategory, type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import {
  CREDIT_USAGE_CATEGORY_COLOR_MAP,
  formatCreditUsage,
  formatStorageUsage,
} from '~/components/CreditUsageMeter/utils';
import { useAnimatedNumber } from '~/hooks/useAnimatedNumber';

const LABEL_MAP = {
  storage: 'File storage',
  smartStorage: 'AI enrichment',
  generative: 'AI generation',
} as const satisfies Record<CreditUsageCategory, string>;

export type CreditUsageBreakdownCategoryProps = Pick<ComponentProps<'div'>, 'className'> & {
  category: CreditUsageCategory;
} & CreditUsageStats['used'][CreditUsageCategory];

export const CreditUsageBreakdownCategory = memo(
  ({ category, className, ...usage }: CreditUsageBreakdownCategoryProps) => {
    const animatedCredits = useAnimatedNumber(usage.credits);
    const animatedGb = useAnimatedNumber('gb' in usage && usage.gb ? usage.gb : null);

    const usageString = useMemo(() => {
      const components: string[] = [];

      if (animatedGb) {
        components.push(formatStorageUsage(animatedGb));
      }

      components.push(
        `${formatCreditUsage(animatedCredits ?? usage.credits, { useLessThanIndicatorForSmallValues: true })} spent`,
      );

      return components.join(' · ');
    }, [animatedCredits, animatedGb, usage.credits]);

    return (
      <div key={category} className="flex items-center justify-between gap-1 text-12">
        <div className="flex items-center gap-1">
          <span className={cn('size-1.5 rounded-full', CREDIT_USAGE_CATEGORY_COLOR_MAP[category])} />
          <span className="truncate text-gray-9">{LABEL_MAP[category]}</span>
        </div>

        <span className="truncate">{usageString}</span>
      </div>
    );
  },
);

CreditUsageBreakdownCategory.displayName = 'CreditUsageBreakdownCategory';
